import { Chip, Tooltip } from "@legal-500/legal500-design-system";
import type { Column } from "@legal-500/legal500-design-system";
import type { CampaignRecipient } from "@/api/campaigns.service";
import {
  FAILED_STATUS_HINT,
  RESENDABLE_SEND_STATUS,
  SENT_STATUS_HINT,
  UNCONFIRMED_STATUS_HINT,
  isSegmentActive,
  mergedStatusChip,
  mergedStatusOf,
  responseStateChip,
  responseStateOf,
} from "./recipient-status";
import styles from "./MailingGroups.module.css";

// Row shape passed to the table: the projection has no id, so we key by email.
export type RecipientRow = CampaignRecipient & { id: string };

type Options = {
  // "create" = pick candidates to freeze into a send (email-keyed);
  // "resend" = pick submit_failed recipients to re-dispatch (recipient_id-keyed,
  // eligibility-gated so in-flight / non-failed rows can't be selected).
  selectionMode: "create" | "resend";
  allOnPageSelected: boolean;
  // Header checkbox disabled when there is nothing selectable on the page.
  headerDisabled: boolean;
  isRowSelected: (row: RecipientRow) => boolean;
  isRowSelectable: (row: RecipientRow) => boolean;
  onTogglePage: () => void;
  onToggleRow: (row: RecipientRow) => void;
};

// Builds the candidate-table columns. The leading column is a checkbox whose
// header toggles the whole page; DS `Column.header` is typed `string`, so the
// ReactNode header is cast the same way `DataTable` casts sortable headers.
export function buildRecipientColumns({
  selectionMode,
  allOnPageSelected,
  headerDisabled,
  isRowSelected,
  isRowSelectable,
  onTogglePage,
  onToggleRow,
}: Options): Column<RecipientRow>[] {
  return [
    {
      header: (
        <input
          type="checkbox"
          aria-label="Select page"
          checked={allOnPageSelected}
          disabled={headerDisabled}
          onChange={onTogglePage}
        />
      ) as unknown as string,
      accessor: "id",
      render: (_v, row) => {
        const selectable = isRowSelectable(row);
        return (
          <input
            type="checkbox"
            aria-label={`Select ${row.email}`}
            checked={selectable && isRowSelected(row)}
            disabled={!selectable}
            // Hint WHY a row is locked. Resend mode locks non-failed rows and failed
            // ones whose claiming segment is still running (the API would skip those,
            // so they get the more specific reason); create mode locks recipients
            // already claimed by this campaign (per-campaign dedup — a new send would
            // silently skip them), opted-out / undeliverable emails, and
            // fully-responded referees (no survey links left to send). Only shown
            // while the row is actually locked; already-in-campaign takes message
            // priority (it matches the status chip the operator sees), then opted-out.
            title={
              !selectable
                ? selectionMode === "resend"
                  ? row.send_status === RESENDABLE_SEND_STATUS &&
                    isSegmentActive(row.segment_status)
                    ? "Its send is still running — wait for the segment to finish, then resend"
                    : "Only failed recipients whose send has finished can be resent"
                  : row.recipient_id
                    ? "Already in this campaign — each recipient receives at most one email per campaign. Use a reminder campaign to contact them again."
                    : row.opted_out
                      ? "Opted out / undeliverable — excluded from sends"
                      : "Fully responded — no survey links left to send"
                : undefined
            }
            onChange={() => onToggleRow(row)}
          />
        );
      },
    },
    { header: "Email", accessor: "email" },
    { header: "Name", accessor: "name", render: v => (v as string | null) ?? "—" },
    { header: "Surname", accessor: "surname", render: v => (v as string | null) ?? "—" },
    {
      header: "Regions",
      accessor: "regions",
      wrap: true,
      render: v => (v as string[]).join(", ") || "—",
    },
    {
      header: "Firms",
      accessor: "firms",
      wrap: true,
      render: v => (v as string[]).join(", ") || "—",
    },
    {
      // Single merged status: send_status (real send state) folded together
      // with the projection status into one business-friendly chip.
      header: "Status",
      accessor: "status",
      render: (_v, row) => {
        const merged = mergedStatusOf(row);
        const chip = mergedStatusChip(merged);
        const chipEl = (
          <Chip
            size="small"
            color={chip.color}
            className={chip.color === "default" ? styles.neutralStatusChip : undefined}
          >
            <span
              className={styles.statusChip}
              // "Sent" only means SendGrid accepted the message — a native title
              // keeps that caveat one hover away without a heavier tooltip.
              title={merged === "sent" ? SENT_STATUS_HINT : undefined}
            >
              <span
                className={styles.statusDot}
                style={{ backgroundColor: chip.dotColor }}
                aria-hidden="true"
              />
              {chip.label}
            </span>
          </Chip>
        );
        // Failed is a PERMANENT SendGrid rejection — explain that resending only
        // helps after the root cause is fixed. Unconfirmed is a send with an
        // unknown outcome — explain the manual check. Other statuses need no context.
        if (merged === "failed" || merged === "unconfirmed") {
          return (
            <Tooltip
              title={merged === "failed" ? "Delivery failed" : "Delivery unconfirmed"}
              // Reset the table cell's inherited `white-space: nowrap` so the hint
              // wraps inside the tooltip's 200px max-width instead of overflowing.
              content={
                <span style={{ whiteSpace: "normal" }}>
                  {merged === "failed" ? FAILED_STATUS_HINT : UNCONFIRMED_STATUS_HINT}
                </span>
              }
              position="left"
            >
              {chipEl}
            </Tooltip>
          );
        }
        return chipEl;
      },
    },
    {
      // Survey-response coverage — an ORTHOGONAL axis to the send Status chip.
      // Shows how many of this referee's survey links they have responded to as
      // an "M/N" fraction; the colour cue is redundant to that text.
      header: "Response",
      accessor: "responded_count",
      render: (_v, row) => {
        const state = responseStateOf(row);
        const chip = responseStateChip(state);
        const label = `${row.responded_count}/${row.total_count}`;
        return (
          <Tooltip
            title="Survey response"
            content={
              <span style={{ whiteSpace: "normal" }}>
                Responded to {row.responded_count} of {row.total_count} survey link
                {row.total_count === 1 ? "" : "s"}.
              </span>
            }
            position="left"
          >
            <Chip
              size="small"
              color={chip.color}
              className={chip.color === "default" ? styles.neutralStatusChip : undefined}
            >
              <span className={styles.statusChip}>
                <span
                  className={styles.statusDot}
                  style={{ backgroundColor: chip.dotColor }}
                  aria-hidden="true"
                />
                {label}
              </span>
            </Chip>
          </Tooltip>
        );
      },
    },
  ];
}
